/**
 * RegistrationProgress Component
 *
 * Shows real-time progress during vendor registration.
 * Displays status of each step: user creation, vendor setup, linking, etc.
 */

import { Stack, Center } from '@/components/ui/layout';
import { Text, Title } from '@/components/ui/core';
import { Loader, Progress } from '@/components/ui/feedback';
import { Card } from '@/components/ui/data-display';
import { IconCheck, IconLoader2, IconCircle } from '@tabler/icons-react';
import { FadeIn } from '@/components/animations';

export type RegistrationStep =
  | 'creating_account'
  | 'setting_up_vendor'
  | 'linking_account'
  | 'finalizing';

interface RegistrationProgressProps {
  currentStep: RegistrationStep;
}

const STEPS: { key: RegistrationStep; label: string }[] = [
  { key: 'creating_account', label: 'Creating your account' },
  { key: 'setting_up_vendor', label: 'Setting up vendor profile' },
  { key: 'linking_account', label: 'Linking your account' },
  { key: 'finalizing', label: 'Finalizing setup' },
];

function getStepIndex(step: RegistrationStep): number {
  return STEPS.findIndex(s => s.key === step);
}

function getProgressPercentage(step: RegistrationStep): number {
  const index = getStepIndex(step);
  // Each step is 25%, but we show progress during the step
  return (index + 1) * 25 - 12; // -12 to show "in progress" state
}

interface StepItemProps {
  label: string;
  status: 'completed' | 'in_progress' | 'pending';
}

function StepItem({ label, status }: StepItemProps) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
      <div
        style={{
          width: 28,
          height: 28,
          borderRadius: '50%',
          background:
            status === 'completed'
              ? 'var(--mantine-color-green-6)'
              : status === 'in_progress'
              ? 'var(--mantine-color-indigo-6)'
              : 'var(--mantine-color-gray-3)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          transition: 'background 0.3s ease',
        }}
      >
        {status === 'completed' && <IconCheck size={16} color="white" />}
        {status === 'in_progress' && (
          <IconLoader2
            size={16}
            color="white"
            style={{ animation: 'spin 1s linear infinite' }}
          />
        )}
        {status === 'pending' && <IconCircle size={12} color="var(--mantine-color-gray-5)" />}
      </div>
      <Text
        size="sm"
        fw={status === 'in_progress' ? 600 : 400}
        c={status === 'pending' ? 'dimmed' : undefined}
        style={{ transition: 'all 0.3s ease' }}
      >
        {label}
        {status === 'in_progress' && '...'}
      </Text>
    </div>
  );
}

export function RegistrationProgress({ currentStep }: RegistrationProgressProps) {
  const currentIndex = getStepIndex(currentStep);
  const progressValue = getProgressPercentage(currentStep);

  return (
    <FadeIn delay={100}>
      <Stack gap="xl" align="center">
        {/* Animated Icon */}
        <Center>
          <div
            style={{
              width: 80,
              height: 80,
              borderRadius: '50%',
              background: 'linear-gradient(135deg, #4f46e5, #7c3aed)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '0 8px 32px rgba(79, 70, 229, 0.3)',
            }}
          >
            <Loader color="white" size="lg" />
          </div>
        </Center>

        {/* Title */}
        <Title order={3} ta="center">
          Creating Your Account
        </Title>

        <Text c="dimmed" size="sm" ta="center">
          Please wait while we set up your vendor account...
        </Text>

        {/* Progress Card */}
        <Card withBorder p="lg" radius="md" w="100%" maw={400}>
          <Stack gap="md">
            {/* Steps */}
            <Stack gap="sm">
              {STEPS.map((step, index) => {
                let status: 'completed' | 'in_progress' | 'pending';
                if (index < currentIndex) {
                  status = 'completed';
                } else if (index === currentIndex) {
                  status = 'in_progress';
                } else {
                  status = 'pending';
                }
                return (
                  <StepItem key={step.key} label={step.label} status={status} />
                );
              })}
            </Stack>

            {/* Progress Bar */}
            <Progress
              value={progressValue}
              size="sm"
              radius="xl"
              color="indigo"
              animated
              mt="sm"
            />
            <Text size="xs" c="dimmed" ta="center">
              {Math.round(progressValue)}% complete
            </Text>
          </Stack>
        </Card>

        <style>{`
          @keyframes spin {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }
        `}</style>
      </Stack>
    </FadeIn>
  );
}
